import { term, holdTerminal, closeTerminal } from './agent-store.js';

// Терминал до сервера в браузере.
//
// Раннер стоит на чужой машине, и когда что-то идёт не так, смотреть надо там:
// логи контейнера, место на диске, живой ли docker.
//
// Сам терминал живёт в хранилище, а не в компоненте: уход на другую вкладку не
// должен рвать открытое соединение. Компонент только показывает его узел и
// возвращает обратно, когда его закрывают.

export const Terminal = {
  props: { host: { type: Object, required: true } },
  data() {
    return {
      auth: 'password',
      password: '',
      privateKey: '',
      passphrase: '',
      error: '',
      dead: false,
      showAuth: false,
    };
  },
  computed: {
    live() { return term.open && term.hostId === this.host.id; },
  },
  mounted() { this.mount(); },
  updated() { this.mount(); },
  methods: {
    /** Возвращаем узел терминала на экран — тот же самый, не новый. */
    mount() {
      const slot = this.$refs.slot;
      if (!slot || !this.live || !term.box) return;
      if (term.box.parentNode !== slot) {
        slot.appendChild(term.box);
        // После переезда размеры надо пересчитать, иначе строки обрежутся.
        setTimeout(() => term.xterm?.resize(term.xterm.cols, term.xterm.rows), 0);
      }
    },

    close() {
      closeTerminal();
      this.$emit('close');
    },

    async open() {
      const byKey = this.auth === 'key';
      // Доступ сохранён при установке — вводить нечего. Не сохранён (сервер
      // ставили до того, как это появилось) — спрашиваем, как раньше.
      if (!this.host.has_auth && (byKey ? !this.privateKey.trim() : !this.password)) {
        this.error = byKey ? 'нужен приватный ключ' : 'нужен пароль';
        return;
      }
      this.error = '';

      const { Terminal: Xterm } = await import(/* @vite-ignore */ '/vendor/xterm.mjs');

      // Свой узел, а не тот, что нарисовал Vue: он переезжает между показами,
      // и Vue не должен его переписывать.
      const box = document.createElement('div');
      box.className = 'term-screen';

      const xterm = new Xterm({
        fontSize: 13,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        cursorBlink: true,
        scrollback: 4000,
        theme: { background: '#12100f', foreground: '#e8e5dc', cursor: '#d97757' },
      });
      xterm.open(box);

      this.connect(xterm, box);
      this.$nextTick(() => { this.mount(); xterm.focus(); });
    },

    /**
     * Открывает сокет и связывает его с экраном. Отдельно от open, потому что
     * тем же кодом переподключаются: сессия на сервере живёт в tmux, и после
     * обрыва достаточно зайти в неё снова.
     */
    connect(xterm, box) {
      const byKey = this.auth === 'key';
      // При переподключении полей уже нет — берём то, чем открывали.
      const typed = byKey
        ? { private_key: this.privateKey, passphrase: this.passphrase }
        : { password: this.password };
      const has = Object.values(typed).some(Boolean);
      const access = has ? typed : (term.access || {});
      const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/terminal`;
      const ws = new WebSocket(url);
      this.dead = false;

      ws.onopen = () => {
        ws.send(JSON.stringify({
          type: 'open',
          host: this.host.id,
          // Пусто — сервер возьмёт сохранённый доступ. Вводят его только те,
          // у кого он не сохранён, или когда пароль сменился.
          password: access.password || '',
          private_key: access.private_key || '',
          passphrase: access.passphrase || '',
          cols: xterm.cols,
          rows: xterm.rows,
        }));
        // Введённое руками в браузере не держим.
        this.password = '';
        this.privateKey = '';
        this.passphrase = '';
      };
      ws.onmessage = (e) => xterm.write(e.data);
      ws.onerror = () => xterm.write('\r\nсоединение с сайтом оборвалось\r\n');
      // Само не переподключаемся нарочно: это доступ к чужому проду, и решение
      // вернуться туда принимает человек, а не таймер.
      ws.onclose = () => { this.dead = true; };

      xterm.onData((d) => { if (ws.readyState === WebSocket.OPEN) ws.send(d); });
      xterm.onResize(({ cols, rows }) => {
        if (ws.readyState === WebSocket.OPEN) ws.send(` resize:${cols}x${rows}`);
      });

      holdTerminal(this.host.id, { box, xterm, ws, access: has ? access : undefined });
      // Ручка для проверок: оборвать соединение ровно так, как его рвёт сеть.
      // Ничего не открывает: сокет и так живёт в этой вкладке.
      window.__hhTermWs = ws;
    },

    /** Вернуться в ту же сессию: пароль для этого больше не нужен. */
    reconnect() {
      if (!term.xterm || !term.box) return;
      term.xterm.write('\r\n\u001b[90mпереподключаюсь…\u001b[0m\r\n');
      this.connect(term.xterm, term.box);
      this.$nextTick(() => { this.mount(); term.xterm.focus(); });
    },
  },
  template: `
    <div class="card term-card">
      <div class="row" style="margin-bottom:12px">
        <h3 style="margin:0;flex:1">
          Терминал · {{ host.login }}@{{ host.host }}<template v-if="host.port !== 22">:{{ host.port }}</template>
        </h3>
        <button class="ghost sm" @click="close">Закрыть</button>
      </div>

      <template v-if="!live">
        <p class="note" style="margin-top:0">
          Сессия живёт на самой машине, в tmux: закрытая вкладка и оборванная
          связь её не убивают — вернёшься в тот же экран. Само соединение
          отпускается после двадцати минут без дела, работа при этом остаётся.
        </p>

        <div class="row" v-if="host.has_auth && !showAuth" style="margin-bottom:12px">
          <button class="primary" @click="open">Подключиться</button>
          <button class="ghost sm" @click="showAuth = true">Другой доступ</button>
          <span class="faint">доступ сохранён при установке</span>
        </div>

        <template v-if="!host.has_auth || showAuth">
        <div class="seg" style="margin-bottom:10px">
          <button :class="{ on: auth === 'password' }" @click="auth = 'password'">Пароль</button>
          <button :class="{ on: auth === 'key' }" @click="auth = 'key'">Приватный ключ</button>
        </div>

        <div v-if="auth === 'password'">
          <label>Пароль</label>
          <input type="password" v-model="password" @keydown.enter="open()"
                 placeholder="уйдёт один раз и не сохранится">
        </div>
        <div v-else>
          <label>Приватный ключ</label>
          <textarea v-model="privateKey" rows="4" class="key"
                    placeholder="-----BEGIN OPENSSH PRIVATE KEY----- … -----END-----"></textarea>
          <div style="margin-top:10px">
            <label>Пароль к ключу <i class="faint">если он зашифрован</i></label>
            <input type="password" v-model="passphrase" placeholder="обычно пусто">
          </div>
        </div>

        <p class="err" v-if="error">{{ error }}</p>
        <div class="row" style="margin-top:12px">
          <button class="primary" @click="open">Подключиться</button>
        </div>
        </template>
      </template>

      <div ref="slot" v-show="live"></div>

      <!-- Соединение отпущено, а сессия на сервере жива: вернуться в неё —
           одна кнопка, без пароля. Само не переподключается нарочно. -->
      <div class="row" v-if="live && dead" style="margin-top:12px">
        <button class="primary" @click="reconnect">Переподключиться</button>
        <span class="faint">соединение закрыто, сессия на сервере жива</span>
      </div>
    </div>`,
};
